import React from 'react';
import { Link2, ShoppingBag } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';

export const IntegracjePage: React.FC = () => {
    usePageMetadata({
        title: "Integracje AI z Allegro i Shopify | Automatyzacja Sprzedaży",
        description: "Zautomatyzuj sprzedaż, obsługę i marketing na Allegro i Shopify. Pracujemy nad dedykowanymi integracjami AI. Zgłoś swoje potrzeby i bądź pierwszy!",
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Integracje Allegro / Shopify (MVP)",
            "serviceType": "Automatyzacja E-commerce",
            "provider": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl"
            },
            "description": "Dedykowane rozwiązania do integracji i automatyzacji procesów na popularnych platformach e-commerce jak Allegro i Shopify. Produkt w fazie rozwoju.",
            "url": "https://biznesbezklikania.pl/rozwiazania/integracje"
        }
    });

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="blue" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                            <Link2 className="w-10 h-10 text-white" />
                        </div>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
                            <ShoppingBag className="w-4 h-4 text-amber-500" />
                            <span className="text-sm font-medium text-amber-500">MVP w budowie</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            Integracje Allegro / Shopify
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-10">
                            Dedykowane rozwiązania do integracji i automatyzacji procesów na najpopularniejszych platformach e-commerce. Produkt w fazie rozwoju.
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <LeadFormModal
                            productSlug="integracje"
                            triggerLabel="Zgłoś swoje zapotrzebowanie"
                            triggerClassName="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group animate-glow"
                        />
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Coming Soon Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 max-w-3xl relative z-10">
                    <RevealOnScroll>
                        <GlassCard variant="strong" className="text-center">
                            <h2 className="text-3xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
                                Wkrótce dostępne
                            </h2>
                            <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                Pracujemy nad gotowymi modułami, które pozwolą Ci w pełni zautomatyzować procesy sprzedaży, obsługi klienta i marketingu na platformach Allegro i Shopify. Chcesz, abyśmy uwzględnili Twoje potrzeby? Daj nam znać, czego oczekujesz od idealnej integracji!
                            </p>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/integracje" />
        </>
    );
};
